import type { DashboardStats } from './types';
import { Card, CardContent } from '@/components/ui/card';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useMemo } from 'react';

interface Props {
  stats: DashboardStats | null;
  loading: boolean;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ dataKey: string; name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload) return null;
  return (
    <div className="rounded-lg border border-border bg-popover p-2.5 text-xs shadow-lg">
      <p className="mb-1 font-medium text-foreground">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium text-foreground">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

export function ActivityTimeline({ stats, loading }: Props) {
  const data = useMemo(() => {
    if (!stats?.dailyActivity) return [];
    return stats.dailyActivity.map((d) => ({
      date: formatDate(d.date),
      count: d.count,
    }));
  }, [stats?.dailyActivity]);

  if (loading) {
    return (
      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground">Aktivitätsverlauf (30 Tage)</h3>
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="h-[260px] animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-foreground">Aktivitätsverlauf (30 Tage)</h3>
      <Card className="border-border">
        <CardContent className="p-4">
          {data.length === 0 ? (
            <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
              Keine Aktivität im Zeitraum.
            </div>
          ) : (
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 16%, 85%)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'hsl(215, 12%, 45%)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(214, 16%, 85%)' }}
                    interval={4}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'hsl(215, 12%, 45%)' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="Aktivitäten"
                    stroke="hsl(174, 72%, 38%)"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
